import React from 'react';
import { RegulatoryFramework, RegulatoryAuthority } from '../types';

interface RegulatoryFrameworksListProps {
  frameworks: RegulatoryFramework[];
  authorities: RegulatoryAuthority[];
  selectedAuthority: string;
  onSelectAuthority: (authId: string) => void;
  onSelectFramework: (frameworkId: string) => void;
}

export const RegulatoryFrameworksList: React.FC<RegulatoryFrameworksListProps> = ({
  frameworks,
  authorities,
  selectedAuthority,
  onSelectAuthority,
  onSelectFramework,
}) => {
  const filtered = frameworks.filter((f) => {
    if (selectedAuthority && f.authorityId !== selectedAuthority) return false;
    return true;
  });

  const getNormativeBadge = (level: string) => {
    switch (level) {
      case 'BINDING':
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200">ZORUNLU / BINDING</span>;
      case 'ACCEPTABLE_MEANS':
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-200">KABUL EDILEBILIR ARAC (AMC)</span>;
      case 'GUIDANCE':
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-800 border border-teal-200">REHBER / GUIDANCE</span>;
      default:
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-800">{level}</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Authority Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 bg-white p-3 rounded-lg border border-gray-200 shadow-xs">
        <span className="text-xs font-semibold text-gray-500 mr-2">Otorite Filtresi:</span>
        <button
          onClick={() => onSelectAuthority('')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            selectedAuthority === ''
              ? 'bg-blue-600 text-white shadow-xs'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Tum Otoriteler ({frameworks.length})
        </button>
        {authorities.map((a) => (
          <button
            key={a.id}
            onClick={() => onSelectAuthority(a.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              selectedAuthority === a.id
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {a.code} ({a.name.split(' ')[0]})
          </button>
        ))}
      </div>

      {/* Frameworks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((f) => (
          <div
            key={f.id}
            onClick={() => onSelectFramework(f.id)}
            className="bg-white rounded-lg border border-gray-200 p-5 hover:border-blue-400 hover:shadow-md transition cursor-pointer flex flex-col justify-between"
          >
            <div>
              <div className="flex justify-between items-start mb-2">
                <span className="font-mono text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                  {f.code}
                </span>
                {getNormativeBadge(f.normativeLevel)}
              </div>
              <h3 className="text-sm font-bold text-gray-900 mt-1 line-clamp-2">{f.name}</h3>
              <p className="text-xs text-gray-500 mt-2 line-clamp-3 leading-relaxed">
                {f.description || 'Aciklama belirtilmemis.'}
              </p>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-800">{f.authorityCode}</span>
                <span>•</span>
                <span className="text-gray-500">{f.category.replace('_', ' ')}</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-500 font-mono text-[11px]">
                <span>{f.documentsCount || 0} Dokuman</span>
                <span>{f.requirementsCount || 0} Kural</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
